import React, { Fragment, useState, Suspense, useEffect } from 'react';
import { Formik } from 'formik';
import {
  ProjectConfig,
  WeldOrganization,
  SaveWeldProgramConfigPayload,
  SaveWeldProgramConfigInput,
} from '@bechtel/types';
import {
  Box,
  Text,
  Radio,
  ButtonAppearance,
  Spinner,
  SpinnerSize,
  ToastType,
  ToastAction,
  useToastsDispatch,
  ToggleSwitch,
  TextFieldSize,
} from '@bechtel/gds';
import { GET_WELD_PROGRAM_MGMT_CONFIG, SAVE_WELD_PROGRAM_CONFIG } from '@bechtel/client-gql'; //TODO: Will remove in data ticket//
import { NetworkStatus, useQuery, useMutation } from '@apollo/client';
import { useAppUser, ErrorRetry, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { SaveWeldProgramConfigErrorExt } from './error/types';
import {
  ProjectAdminContainerStyled,
  ProjectAdminSubContainerStyled,
  FooterStyled,
  FooterButtonStyled,
} from '../../styled';
import { get, map } from 'lodash';
import { weldOrgList } from './form-data';
import WeldProgramMgmtError from '~/quantity-reporting/weld-program-mgmt/error';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

export interface FormikState {
  fitUpWorkflowEnabled: boolean;
  weldOrganization: WeldOrganization;
}

const WeldProgramManagementUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Weld program management data saved successfully.</Trans>
      </Text>
    </Box>
  );
};

let formikInitialValues: FormikState = {
  fitUpWorkflowEnabled: true,
  weldOrganization: WeldOrganization.WELD_POOL,
};

export const WeldProgramMgmt = () => {
  const { user } = useAppUser();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const dispatchToast = useToastsDispatch();
  const [saveWeldConfigError, setSaveWeldConfigError] = useState<SaveWeldProgramConfigErrorExt | null>();
  const { loading, error, data, refetch, networkStatus } = useQuery<{
    projectConfig: ProjectConfig;
  }>(GET_WELD_PROGRAM_MGMT_CONFIG, {
    variables: { id: user?.project?.id },
    notifyOnNetworkStatusChange: true,
  });
  const projectConfigData: ProjectConfig = get(data, 'projectConfigById', undefined);
  const { i18n } = useLingui();

  useEffect(() => {
    if (!projectConfigData) return;
    formikInitialValues = {
      ...formikInitialValues,
      ...projectConfigData.weldConfig,
    };
    setInitialValues(formikInitialValues);
  }, [projectConfigData]);

  const [saveWeldProgramConfig, { loading: mutationLoading }] = useMutation<
    { saveWeldProgramConfigPayload: SaveWeldProgramConfigPayload },
    { input: SaveWeldProgramConfigInput }
  >(SAVE_WELD_PROGRAM_CONFIG, {
    onCompleted: (data) => {
      const saveWeldProgramConfigError = get(data, 'saveWeldProgramConfig.error');
      if (saveWeldProgramConfigError) {
        setSaveWeldConfigError(saveWeldProgramConfigError);
      } else {
        setSaveWeldConfigError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: WeldProgramManagementUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setSaveWeldConfigError(error);
    },
  });

  const closeModal = () => {
    setSaveWeldConfigError(null);
  };

  return (
    <Fragment>
      <ProjectAdminContainerStyled style={{ overflow: 'hidden' }}>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="weld-loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="weld-error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>
                There was a problem loading Weld Program managment data. Please click the button below to try again.
              </Trans>
            </ErrorRetry>
          </div>
        )}

        {initialValues && !loading && !error && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={async (values) => {
              const input: SaveWeldProgramConfigInput = {
                fitUpWorkflowEnabled: values.fitUpWorkflowEnabled,
                weldOrganization: values.weldOrganization,
              };
              await saveWeldProgramConfig({
                variables: { input },
              });
            }}
          >
            {(formikProps) => {
              const { dirty, isValid, setFieldValue, values, handleReset, handleSubmit } = formikProps;
              return (
                <Fragment>
                  <ProjectAdminSubContainerStyled data-testid="weld-container">
                    <Box
                      p={{ sm: '550' }}
                      flexGrow={{ sm: 1 }}
                      flexDirection={{ sm: 'column' }}
                      css={`
                        overflow-y: auto;
                      `}
                    >
                      <Box mb={{ sm: '600' }}>
                        <Text family="graphik" size="500" color="grey.1000" weight="600">
                          <Trans>Fit-Up Request</Trans>
                        </Text>
                      </Box>
                      <Box
                        css={`
                          border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                        `}
                        pb={{ sm: '600' }}
                      >
                        <Box mr={{ sm: '450' }} data-testid="weld-workflow">
                          <ToggleSwitch
                            disabled={true}
                            isToggled={values.fitUpWorkflowEnabled}
                            onToggle={() => {
                              setFieldValue('fitUpWorkflowEnabled', !values.fitUpWorkflowEnabled);
                            }}
                          />
                        </Box>
                        <Text family="graphik" size="300" color="grey.1000">
                          <Trans>Use PipeFighter to manage Fit-Up request workflow before completion of welds.</Trans>
                        </Text>
                      </Box>
                      <Box my={{ sm: '600' }}>
                        <Text family="graphik" size="500" color="grey.1000" weight="600">
                          <Trans>Weld Organization</Trans>
                        </Text>
                      </Box>
                      <Box mb={{ sm: '500' }}>
                        <Text family="graphik" size="300" color="grey.1000">
                          <Trans>How are welders organized within your project’s weld program?</Trans>
                        </Text>
                      </Box>
                      <Box flexDirection={{ sm: 'column' }}>
                        {map(weldOrgList, (item) => {
                          return (
                            <Box
                              onClick={() => {
                                setFieldValue('weldOrganization', item.value);
                              }}
                              key={item.value}
                              alignItems={{ sm: 'center' }}
                              mb={{ sm: '550' }}
                            >
                              <Box mr={{ sm: '500' }}>
                                <Radio
                                  size={TextFieldSize.MEDIUM}
                                  readOnly
                                  checked={values.weldOrganization === item.value}
                                />
                              </Box>
                              <Text
                                data-testid={`weld-org-${item.value}`}
                                family="graphik"
                                size="300"
                                color="grey.1000"
                              >
                                {i18n._(item.label)}
                              </Text>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </ProjectAdminSubContainerStyled>
                  {!loading && !error && (
                    <FooterStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <FooterButtonStyled
                            data-testid="weld-cancel"
                            disabled={mutationLoading || !dirty}
                            onClick={() => {
                              handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </FooterButtonStyled>
                        </Box>
                        <FooterButtonStyled
                          data-testid="weld-save"
                          onClick={() => {
                            handleSubmit();
                          }}
                          disabled={mutationLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {mutationLoading && <Spinner />}
                          {!mutationLoading && t`Save Changes`}
                        </FooterButtonStyled>
                      </Box>
                    </FooterStyled>
                  )}
                </Fragment>
              );
            }}
          </Formik>
        )}
      </ProjectAdminContainerStyled>
      {saveWeldConfigError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldProgramMgmtError error={saveWeldConfigError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
